import { useEffect } from 'react';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { DEFAULT_PERMISSIONS } from 'core/global.variables';
import { editMultiGroupGlcPermissions } from 'api/permissions/permissions';
import { useSelectedGroupGlcPermission } from 'hooks/useSelectedGroupGlcPermission/useSelectedGroupGlcPermission';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useModalState } from 'hooks/useModalState/useModalState';

import { EditGroupGlcPermissions } from './EditGroupGlcPermissions';
import {
  EditGroupGlcPermissionsContainerProps,
  EditGroupGlcPermissionsFormBody,
} from './EditGroupGlcPermissions.types';
import { useEditGroupGlcPermissionsValidation } from './EditGroupGlcPermissions.validation';

export const EditGroupGlcPermissionsContainer = ({
  checkedPermissions,
  groupId,
}: EditGroupGlcPermissionsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedGroupGlcPermission, setSelectedGroupGlcPermission } = useSelectedGroupGlcPermission();

  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const { mutate: onEditMultiGroupGlcPermissions, isLoading } = useMutation(editMultiGroupGlcPermissions, {
    onSuccess: (_, { body }) => {
      if (selectedGroupGlcPermission && checkedPermissions.some(({ id }) => id === selectedGroupGlcPermission?.id)) {
        setSelectedGroupGlcPermission({ ...selectedGroupGlcPermission, ...body });
      }

      queryClient.invalidateQueries([QueryKeyEnum.GROUP_GLC_PERMISSIONS_SERVERS]);
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_GLC_PERMISSIONS]);
      toast.dark(formatMessage({ id: AppMessages['permissions.multiAction.edit.success'] }));
      onModalClose();
    },
  });

  const { schema } = useEditGroupGlcPermissionsValidation();
  const formMethods = useForm<EditGroupGlcPermissionsFormBody>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      ...DEFAULT_PERMISSIONS,
    },
  });

  useEffect(() => {
    formMethods.reset();
  }, [isModalOpen]);

  const onSubmit = (formBody: EditGroupGlcPermissionsFormBody) => {
    onEditMultiGroupGlcPermissions({
      id: groupId,
      body: {
        ...formBody,
        idPermissions: checkedPermissions.map(({ id }) => `${id}`),
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <EditGroupGlcPermissions
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
        checkedPermissions={checkedPermissions}
      />
    </FormProvider>
  );
};
